import { z } from 'zod';

/**
 * Source-file / import-job / staging-row DTOs.
 *
 * Mirrors:
 *   - docs/specs/06-ui-api-spec.md §5 (Source Files / Imports API)
 *   - docs/specs/04-import-logic-spec.md §§2,9 (row types, statuses, validation)
 *
 * All kg fields are JSON strings — never coerce to number; see lib/kg.ts.
 */

export const sourceFileTypes = [
  'operation_workbook',
  'ups_estimate_workbook',
  'price_reference_docx',
  'evidence_file',
  'other',
] as const;

export const sourceFileTypeEnum = z.enum(sourceFileTypes);
export type SourceFileType = z.infer<typeof sourceFileTypeEnum>;

export const parsedStatusEnum = z.enum(['not_parsed', 'parsed', 'failed']);
export type ParsedStatus = z.infer<typeof parsedStatusEnum>;

export const sourceFileSchema = z.object({
  source_file_id: z.string(),
  monthly_batch_id: z.string(),
  source_file_type: sourceFileTypeEnum,
  original_file_name: z.string(),
  file_hash: z.string(),
  uploaded_by: z.string(),
  uploaded_at: z.string(),
  parsed_status: parsedStatusEnum,
  company_file_id: z.string().nullable().optional(),
  file_source: z.enum(['legacy', 'local_upload', 'library_reuse']).optional(),
});

export type SourceFileDto = z.infer<typeof sourceFileSchema>;

export const importTypes = [
  'operation_workbook',
  'ups_estimate_workbook',
  'price_reference_docx',
] as const;

export const importTypeEnum = z.enum(importTypes);
export type ImportType = z.infer<typeof importTypeEnum>;

export const importJobStatusValues = [
  'pending',
  'running',
  'validation_failed',
  'ready_to_promote',
  'promoted',
  'failed',
  'cancelled',
] as const;

export const importJobStatusEnum = z.enum(importJobStatusValues);
export type ImportJobStatus = z.infer<typeof importJobStatusEnum>;

/**
 * Import job summary. Backend is source of truth (see backend
 * imports/service.rs `stage_parsed_workbook`); we mirror its key names.
 */
export const importJobSummarySchema = z.object({
  // Row counts.
  inbound_rows: z.number().optional(),
  dismantling_rows: z.number().optional(),
  summary_rows: z.number().optional(),
  // kg totals (strings, never numbers).
  inbound_total_kg: z.string().optional(),
  dismantling_total_kg: z.string().optional(),
  summary_inbound_total_kg: z.string().optional(),
  summary_dismantling_total_kg: z.string().optional(),
  // Error/warn counters as written by backend (P1-#8).
  inbound_errors: z.number().optional(),
  dismantling_errors: z.number().optional(),
  category_mismatches: z.number().optional(),
  workbook_warnings: z.number().optional(),
  // Promotion summary (filled by promote.rs).
  inbound_promoted: z.number().optional(),
  dismantling_promoted: z.number().optional(),
  promoted_at: z.string().optional(),
  parsed_at: z.string().optional(),
  // Failed jobs expose only stable/localizable diagnostics; raw parser or
  // storage errors remain server-side.
  error_code: z.enum(['IMPORT_SOURCE_UNREADABLE', 'IMPORT_PARSE_FAILED']).optional(),
  message_key: z.string().optional(),
});

export type ImportJobSummary = z.infer<typeof importJobSummarySchema>;

export const importJobSchema = z.object({
  import_job_id: z.string(),
  monthly_batch_id: z.string(),
  source_file_id: z.string(),
  import_type: z.string(),
  status: importJobStatusEnum,
  started_at: z.string().nullable(),
  finished_at: z.string().nullable(),
  summary: importJobSummarySchema,
});

export type ImportJobDto = z.infer<typeof importJobSchema>;

export const stagingRowTypes = [
  'inbound_line',
  'dismantling_output',
  'monthly_summary',
  'ups_scenario',
  'price_reference',
] as const;

export const stagingRowTypeEnum = z.enum(stagingRowTypes);
export type StagingRowType = z.infer<typeof stagingRowTypeEnum>;

export const stagingRowStatusValues = [
  'pending',
  'valid',
  'warning',
  'error',
  'corrected',
  'ignored',
  'promoted',
] as const;

export const stagingRowStatusEnum = z.enum(stagingRowStatusValues);
export type StagingRowStatus = z.infer<typeof stagingRowStatusEnum>;

export const validationSeverityEnum = z.enum(['info', 'warning', 'error', 'critical']);
export type ValidationSeverity = z.infer<typeof validationSeverityEnum>;

export const validationMessageSchema = z.object({
  severity: validationSeverityEnum,
  code: z.string(),
  message: z.string(),
  cell: z.string().optional(),
  /** Structured field slug (spec-13 §8.5), e.g. `kg` — localized via
   * `efs.issues.import_fields.<field>` (ns 'issues'). */
  field: z.string().optional(),
});

export type ValidationMessage = z.infer<typeof validationMessageSchema>;

export const stagingRowSchema = z.object({
  staging_row_id: z.string(),
  import_job_id: z.string(),
  row_type: stagingRowTypeEnum,
  source_sheet_name: z.string().nullable(),
  source_row_number: z.number().nullable(),
  raw_payload: z.record(z.unknown()),
  normalized_payload: z.record(z.unknown()).nullable(),
  status: stagingRowStatusEnum,
  // P1-#6: backend always writes `[]` going forward; nullable fallback
  // keeps legacy NULL rows from blowing up the table renderer.
  validation_messages: z
    .array(validationMessageSchema)
    .nullable()
    .transform((v) => v ?? []),
  promoted_record_type: z.string().nullable(),
  promoted_record_id: z.string().nullable(),
});

export type StagingRowDto = z.infer<typeof stagingRowSchema>;

/**
 * Promote outcome derived from the post-promotion `ImportJobDto.summary`
 * (P1-#8). Backend returns the full job DTO; callers read summary keys
 * directly. Keeping this type as a thin alias avoids breaking imports.
 */
export interface PromoteResult {
  inbound_promoted?: number;
  dismantling_promoted?: number;
  promoted_at?: string;
}

export interface StagingRowListMeta {
  total: number;
  page: number;
  per_page: number;
}

export interface StagingRowListResult {
  rows: StagingRowDto[];
  meta: StagingRowListMeta;
}

export interface StagingRowListParams {
  row_type?: StagingRowType;
  status?: StagingRowStatus;
  page?: number;
  per_page?: number;
}
